import React from 'react';
import { twMerge } from 'tailwind-merge';

interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'primary' | 'secondary' | 'outline';
  size?: 'sm' | 'md' | 'lg';
  glow?: boolean;
  children: React.ReactNode;
  href?: string;
}

const variants = {
  primary:
    'bg-gradient-to-r from-primary to-primary-hover text-white hover:shadow-[0_0_40px_rgba(249,115,22,0.4)] active:scale-95',
  secondary:
    'bg-white/10 text-white border border-white/20 hover:bg-white/20 active:scale-95',
  outline:
    'bg-transparent text-white border-2 border-white/30 hover:border-white/60 hover:bg-white/5 active:scale-95',
};

const sizes = {
  sm: 'px-4 py-2 text-sm',
  md: 'px-6 py-3 text-base',
  lg: 'px-8 py-4 text-lg',
};

export default function Button({
  variant = 'primary',
  size = 'md',
  glow = false,
  children,
  className,
  href,
  ...props
}: ButtonProps) {
  const classes = twMerge(
    'inline-flex items-center justify-center gap-2 rounded-xl font-semibold transition-all duration-300 cursor-pointer',
    variants[variant],
    sizes[size],
    glow && 'animate-glow-pulse',
    className
  );

  if (href) {
    const isAnchor = href.startsWith('#');
    return (
      <a
        href={href}
        className={classes}
        {...(!isAnchor && { target: '_blank', rel: 'noopener noreferrer' })}
      >
        {children}
      </a>
    );
  }

  return (
    <button className={classes} {...props}>
      {children}
    </button>
  );
}
